import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadialBarChart, RadialBar, Legend } from 'recharts';
import IkiSymbol from '@/components/IkiSymbol';
import { Spark, IntegrationIcon } from '@/components/HypIcons';
import '@/styles/YourGrowth.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function WeeklySummaryCard({ userId }) {
  const [summary, setSummary] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [weeks, setWeeks] = useState(1);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (userId) loadLatest();
  }, [userId]);

  const loadLatest = async () => {
    try {
      const res = await axios.get(`${API}/training/weekly-summary/${userId}/latest`);
      if (res.data.exists) setSummary(res.data);
    } catch (e) {
      // no summary yet
    } finally {
      setLoaded(true);
    }
  };

  const generate = async () => {
    setIsGenerating(true);
    try {
      const res = await axios.post(`${API}/training/weekly-summary/${userId}/generate?weeks=${weeks}`);
      setSummary(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Could not generate summary.';
      alert(detail);
    } finally {
      setIsGenerating(false);
    }
  };

  const trendIcon = { improving: '↑', stable: '→', declining: '↓' };
  const trendColor = { improving: '#1E6B68', stable: '#2D8B87', declining: '#C0392B' };

  const cardStyle = {
    background: 'linear-gradient(135deg, rgba(247,243,236,0.95) 0%, rgba(239,231,221,0.95) 100%)',
    borderRadius: '20px',
    border: '1px solid rgba(30,107,104,0.12)',
    boxShadow: '0 4px 24px rgba(30,107,104,0.08)',
    padding: '28px',
    marginBottom: '24px'
  };

  return (
    <div style={cardStyle}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#1E6B68', fontFamily: "'Playfair Display', serif", fontSize: '1.3rem' }}>
            Weekly Insight
          </h2>
          <p style={{ margin: '4px 0 0', color: '#888', fontSize: '0.82rem' }}>
            AI analysis of your internal patterns
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <select
            value={weeks}
            onChange={(e) => setWeeks(Number(e.target.value))}
            style={{ padding: '8px 12px', borderRadius: '10px', border: '1.5px solid rgba(30,107,104,0.2)', background: '#fff', color: '#1E6B68', fontSize: '0.82rem' }}
          >
            <option value={1}>Last week</option>
            <option value={2}>Last 2 weeks</option>
            <option value={4}>Last month</option>
          </select>
          <button
            onClick={generate}
            disabled={isGenerating}
            style={{
              padding: '8px 18px',
              background: isGenerating ? 'rgba(30,107,104,0.4)' : 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '20px',
              fontSize: '0.82rem',
              cursor: isGenerating ? 'not-allowed' : 'pointer',
              fontWeight: 500,
              whiteSpace: 'nowrap'
            }}
          >
            {isGenerating ? 'Generating…' : summary ? '↺ Refresh' : 'Generate'}
          </button>
        </div>
      </div>

      {/* Loading state */}
      {isGenerating && (
        <div style={{ textAlign: 'center', padding: '32px', color: '#2D8B87' }}>
          <div style={{ fontSize: '1.8rem', marginBottom: '12px', animation: 'pulse 2s ease-in-out infinite' }}>◎</div>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>HYPSEYA is reading your week…</p>
        </div>
      )}

      {/* No summary yet */}
      {!isGenerating && !summary && loaded && (
        <div style={{ textAlign: 'center', padding: '32px', color: '#bbb' }}>
          <div style={{ fontSize: '2rem', marginBottom: '10px' }}>◎</div>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>
            Generate your first weekly insight to see patterns in your practice.
          </p>
        </div>
      )}

      {/* Summary content */}
      {!isGenerating && summary?.summary && (
        <div>
          {/* Headline */}
          <p style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '1.15rem',
            color: '#1E6B68',
            margin: '0 0 16px',
            fontStyle: 'italic',
            lineHeight: 1.4
          }}>
            "{summary.summary.headline}"
          </p>

          {/* Stats row */}
          {summary.stats && (
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
              {summary.stats.total_sessions !== undefined && (
                <div style={{ padding: '8px 14px', background: 'rgba(30,107,104,0.07)', borderRadius: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1E6B68' }}>{summary.stats.total_sessions}</div>
                  <div style={{ fontSize: '0.68rem', color: '#888', marginTop: '2px' }}>sessions</div>
                </div>
              )}
              {summary.stats.eri && (
                <div style={{ padding: '8px 14px', background: 'rgba(30,107,104,0.07)', borderRadius: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1E6B68' }}>
                    {summary.stats.eri.toFixed(0)}
                    {summary.stats.eri_trend && (
                      <span style={{ color: trendColor[summary.stats.eri_trend], fontSize: '0.9rem' }}>
                        {' '}{trendIcon[summary.stats.eri_trend]}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.68rem', color: '#888', marginTop: '2px' }}>ERI</div>
                </div>
              )}
              {summary.stats.avg_delta !== null && summary.stats.avg_delta !== undefined && (
                <div style={{ padding: '8px 14px', background: 'rgba(30,107,104,0.07)', borderRadius: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: summary.stats.avg_delta >= 0 ? '#1E6B68' : '#C0392B' }}>
                    {summary.stats.avg_delta > 0 ? '+' : ''}{summary.stats.avg_delta}
                  </div>
                  <div style={{ fontSize: '0.68rem', color: '#888', marginTop: '2px' }}>avg shift</div>
                </div>
              )}
            </div>
          )}

          {/* Narrative */}
          <p style={{ color: '#555', fontSize: '0.9rem', lineHeight: 1.75, margin: '0 0 20px' }}>
            {summary.summary.narrative}
          </p>

          {/* Patterns */}
          {summary.summary.patterns?.length > 0 && (
            <div style={{ marginBottom: '18px' }}>
              <p style={{ margin: '0 0 8px', fontSize: '0.75rem', fontWeight: 600, color: '#888', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Patterns observed
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {summary.summary.patterns.map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <div style={{ width: '6px', height: '6px', background: '#1E6B68', borderRadius: '50%', marginTop: '6px', flexShrink: 0 }} />
                    <p style={{ margin: 0, color: '#555', fontSize: '0.88rem', lineHeight: 1.5 }}>{p}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ERI insight */}
          {summary.summary.eri_insight && (
            <div style={{ padding: '14px 16px', background: 'rgba(30,107,104,0.05)', borderLeft: '3px solid #1E6B68', borderRadius: '0 10px 10px 0', marginBottom: '18px' }}>
              <p style={{ margin: 0, color: '#1E6B68', fontSize: '0.85rem', lineHeight: 1.6 }}>
                {summary.summary.eri_insight}
              </p>
            </div>
          )}

          {/* Recommendation */}
          {summary.summary.recommendation && (
            <div style={{ marginBottom: '16px' }}>
              <p style={{ margin: '0 0 6px', fontSize: '0.75rem', fontWeight: 600, color: '#888', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                For next week
              </p>
              <p style={{ margin: 0, color: '#444', fontSize: '0.88rem', lineHeight: 1.6 }}>
                {summary.summary.recommendation}
              </p>
            </div>
          )}

          {/* Closing */}
          {summary.summary.closing && (
            <p style={{ margin: '16px 0 0', color: '#888', fontSize: '0.82rem', fontStyle: 'italic', borderTop: '1px solid rgba(30,107,104,0.1)', paddingTop: '14px' }}>
              {summary.summary.closing}
            </p>
          )}

          {/* Generated at */}
          {summary.generated_at && (
            <p style={{ margin: '10px 0 0', color: '#ccc', fontSize: '0.72rem' }}>
              Generated {new Date(summary.generated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Commitment Path Tracker ──────────────────────────────────────────────────
const PATHS = [
  { id: 'p7',  days: 7,  label: '7-Day Reset',       color: '#2D8B87', desc: 'Begin. One session per day.' },
  { id: 'p14', days: 14, label: '14-Day Foundation',  color: '#1E6B68', desc: 'Build the daily loop habit.' },
  { id: 'p21', days: 21, label: '21-Day Integration', color: '#155B57', desc: 'Consolidate and integrate.' },
];

function PathTracker({ userId, navigate }) {
  const storageKey = userId ? `hyp_paths_${userId}` : 'hyp_paths_anon';
  const [paths, setPaths] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '{}'); }
    catch { return {}; }
  });

  const startPath = (pathId, days) => {
    const updated = { ...paths, [pathId]: { startedAt: new Date().toISOString(), days } };
    setPaths(updated);
    localStorage.setItem(storageKey, JSON.stringify(updated));
  };

  const getDaysFilled = (startedAt, totalDays) => {
    const start = new Date(startedAt);
    const now = new Date();
    const elapsed = Math.floor((now - start) / 86400000);
    return Math.min(elapsed + 1, totalDays);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {PATHS.map(({ id, days, label, color, desc }) => {
        const active = paths[id];
        const filled = active ? getDaysFilled(active.startedAt, days) : 0;
        const pct = active ? Math.round((filled / days) * 100) : 0;
        const done = pct >= 100;

        return (
          <div key={id} style={{
            background: 'rgba(255,255,255,0.88)',
            borderRadius: '16px',
            padding: '20px 22px',
            border: `1.5px solid ${active ? color + '44' : 'rgba(184,211,207,0.35)'}`,
            boxShadow: '0 2px 12px rgba(30,107,104,0.06)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div>
                <p style={{ margin: 0, fontFamily: "'Playfair Display', serif", fontSize: '1rem', color: color, fontWeight: '400' }}>{label}</p>
                <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#9AADAB' }}>{desc}</p>
              </div>
              {active && !done && (
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: color, background: color + '18', padding: '3px 10px', borderRadius: '20px' }}>
                  Day {filled}/{days}
                </span>
              )}
              {done && (
                <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#2D8B87', background: 'rgba(45,139,135,0.12)', padding: '3px 10px', borderRadius: '20px' }}>
                  ✓ Complete
                </span>
              )}
            </div>

            {/* Day dots */}
            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginBottom: '12px' }}>
              {Array.from({ length: days }).map((_, i) => (
                <div key={i} style={{
                  width: '10px', height: '10px', borderRadius: '50%',
                  background: i < filled ? color : 'rgba(184,211,207,0.35)',
                  transition: 'background 0.3s ease'
                }} />
              ))}
            </div>

            {/* Progress bar */}
            {active && (
              <div style={{ height: '4px', background: 'rgba(184,211,207,0.4)', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: '4px', transition: 'width 0.4s ease' }} />
              </div>
            )}

            {!active && (
              <button
                onClick={() => startPath(id, days)}
                style={{
                  marginTop: '8px', padding: '8px 18px', borderRadius: '20px',
                  border: `1.5px solid ${color}`, background: 'transparent',
                  color: color, fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer'
                }}
              >
                Start this path
              </button>
            )}

            {active && !done && (
              <button
                onClick={() => navigate('/')}
                style={{
                  marginTop: '10px', padding: '8px 18px', borderRadius: '20px',
                  border: 'none', background: color,
                  color: '#fff', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer'
                }}
              >
                Go to today's practice →
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function YourGrowth() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [journalEntries, setJournalEntries] = useState([]);
  const [stats, setStats] = useState({
    alignmentStreak: 0,
    breathworkCompletions: 0,
    voiceAffirmations: 0,
    healingSuiteUsage: 0,
    intentionProgress: 0,
    pathCompletion: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      loadData(parsedUser.user_id);
    } else {
      setLoading(false);
    }
  }, []);

  const loadData = async (userId) => {
    try {
      // Load journal entries for mood/energy/focus trends
      const journalResponse = await axios.get(`${API}/journal-entries?user_id=${userId}`);
      setJournalEntries(journalResponse.data);

      // Load stats from localStorage (these will be tracked incrementally)
      const savedStats = localStorage.getItem(`growth_stats_${userId}`);
      if (savedStats) {
        setStats(JSON.parse(savedStats));
      }
    } catch (error) {
      console.error('Error loading growth data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data from journal entries
  const getMoodTrendData = () => {
    return journalEntries
      .slice(-7) // Last 7 entries
      .map(entry => ({
        date: new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        mood: entry.mood,
        energy: entry.energy,
        focus: entry.focus
      }));
  };

  const getIntentionProgressData = () => {
    const progress = stats.intentionProgress || 0;
    return [
      {
        name: 'Progress',
        value: progress,
        fill: '#1E6B68'
      }
    ];
  };

  if (loading) {
    return (
      <div className="growth-container">
        <div className="loading-state">
          <IkiSymbol name="healing" size={48} />
          <p>Loading your growth data...</p>
        </div>
      </div>
    );
  }

  const trendData = getMoodTrendData();

  return (
    <div className="growth-container">
      {/* Back Button */}
      <button
        onClick={() => window.history.back()}
        style={{
          position: 'fixed',
          top: '20px',
          left: '20px',
          zIndex: 1000,
          padding: '12px 20px',
          background: 'rgba(255, 255, 255, 0.95)',
          color: '#1E6B68',
          border: '2px solid #B8D3CF',
          borderRadius: '24px',
          fontSize: '1rem',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 4px 12px rgba(30, 107, 104, 0.15)',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#1E6B68';
          e.currentTarget.style.color = '#fff';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
          e.currentTarget.style.color = '#1E6B68';
        }}
      >
        ← Back
      </button>

      <div className="growth-header">
        <h1>Your Growth</h1>
        <p className="header-subtitle">
          Track your journey of transformation and self-awareness
        </p>
      </div>

      {/* Weekly AI Summary */}
      <WeeklySummaryCard userId={user?.user_id} />

      {/* Trends Section */}
      <div className="growth-section">
        <h2>Your Emotional Trends</h2>
        <div className="chart-card">
          {trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(184, 211, 207, 0.3)" />
                <XAxis dataKey="date" stroke="#2D8B87" style={{ fontSize: '0.9rem' }} />
                <YAxis domain={[1, 10]} stroke="#2D8B87" style={{ fontSize: '0.9rem' }} />
                <Tooltip 
                  contentStyle={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid rgba(184, 211, 207, 0.3)',
                    borderRadius: '12px'
                  }}
                />
                <Legend />
                <Line type="monotone" dataKey="mood" stroke="#1E6B68" strokeWidth={3} name="Mood" dot={{ fill: '#1E6B68', r: 5 }} />
                <Line type="monotone" dataKey="energy" stroke="#2D8B87" strokeWidth={3} name="Energy" dot={{ fill: '#2D8B87', r: 5 }} />
                <Line type="monotone" dataKey="focus" stroke="#D9CBA3" strokeWidth={3} name="Focus" dot={{ fill: '#D9CBA3', r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-chart">
              <IkiSymbol name="journal" size={48} />
              <p>Start journaling to track your emotional trends</p>
            </div>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="growth-section">
        <h2>Your Practice Stats</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <IkiSymbol name="healing" size={32} />
            </div>
            <div className="stat-value">{stats.alignmentStreak}</div>
            <div className="stat-label">Day Streak</div>
            <div className="stat-description">Daily Alignment</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <IkiSymbol name="calm" size={32} />
            </div>
            <div className="stat-value">{stats.breathworkCompletions}</div>
            <div className="stat-label">Sessions</div>
            <div className="stat-description">Breathwork</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <IkiSymbol name="mic" size={32} />
            </div>
            <div className="stat-value">{stats.voiceAffirmations}</div>
            <div className="stat-label">Plays</div>
            <div className="stat-description">Voice Affirmations</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <IkiSymbol name="healing" size={32} />
            </div>
            <div className="stat-value">{stats.healingSuiteUsage}</div>
            <div className="stat-label">Sessions</div>
            <div className="stat-description">Healing Suite</div>
          </div>
        </div>
      </div>

      {/* Intention Progress */}
      <div className="growth-section">
        <h2>Intention Progress</h2>
        <div className="chart-card">
          <div className="radial-chart-container">
            <ResponsiveContainer width="100%" height={250}>
              <RadialBarChart 
                cx="50%" 
                cy="50%" 
                innerRadius="60%" 
                outerRadius="90%" 
                data={getIntentionProgressData()}
                startAngle={180}
                endAngle={0}
              >
                <RadialBar
                  minAngle={15}
                  background
                  clockWise
                  dataKey="value"
                  cornerRadius={10}
                />
                <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" className="progress-text">
                  <tspan x="50%" dy="-0.5em" fontSize="2rem" fontWeight="600" fill="#1E6B68">
                    {stats.intentionProgress}%
                  </tspan>
                  <tspan x="50%" dy="1.5em" fontSize="0.9rem" fill="#2D8B87">
                    Complete
                  </tspan>
                </text>
              </RadialBarChart>
            </ResponsiveContainer>
            <p className="progress-note">Your journey with {user?.intention || 'your intention'}</p>
          </div>
        </div>
      </div>

      {/* Path Commitment Tracker */}
      <div className="growth-section">
        <h2>Commitment Paths</h2>
        <PathTracker userId={user?.user_id} navigate={navigate} />
      </div>

      {/* ── Integration Report CTA ── */}
      <div
        onClick={() => navigate('/integration-report')}
        style={{
          margin: '0 0 24px',
          background: 'linear-gradient(135deg, rgba(30,107,104,0.06), rgba(184,144,106,0.06))',
          border: '1px solid rgba(30,107,104,0.15)',
          borderLeft: '3px solid #1E6B68',
          borderRadius: '18px',
          padding: '20px 22px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(30,107,104,0.12)'}
        onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '10px', fontWeight: 700, color: '#B8906A',
              letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '5px' }}>
              Monthly · AI Generated
            </div>
            <h3 style={{ margin: '0 0 5px', fontSize: '1rem', fontWeight: 700, color: '#1E3A39' }}>
              Integration Report
            </h3>
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#6B8B87', lineHeight: 1.55 }}>
              A letter from HYPSEYA synthesising your month — patterns, breakthrough, shadow zone, and intention.
            </p>
          </div>
          <span style={{ marginLeft: '16px', fontSize: '22px', flexShrink: 0, opacity: 0.6 }}>✦</span>
        </div>
      </div>

      {/* ── Quick links to new tools ── */}
      <div style={{ display: 'flex', gap: '10px', margin: '0 0 24px', flexWrap: 'wrap' }}>
        {[
          {
            label: 'Proof of Change',
            path: '/proof',
            icon: <Spark size={16} color="#1E6B68" />,
          },
          {
            label: 'Somatic Map',
            path: '/somatic-map',
            icon: (
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <ellipse cx="10" cy="5" rx="3" ry="3" stroke="#1E6B68" strokeWidth="1.4"/>
                <path d="M7 8h6l1 8H6l1-8z" stroke="#1E6B68" strokeWidth="1.4" strokeLinejoin="round"/>
                <path d="M6 11l-2 5M14 11l2 5" stroke="#1E6B68" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
            ),
          },
          {
            label: 'Circles',
            path: '/circles',
            icon: <IntegrationIcon size={16} color="#1E6B68" />,
          },
        ].map(({ label, path, icon }) => (
          <button key={path} onClick={() => navigate(path)} style={{
            flex: '1 1 120px',
            padding: '11px 14px',
            background: 'rgba(255,255,255,0.8)',
            border: '1px solid rgba(30,107,104,0.14)',
            borderRadius: '12px',
            fontSize: '13px', fontWeight: 600,
            color: '#1E6B68', cursor: 'pointer',
            transition: 'all 0.15s',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(30,107,104,0.07)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.8)'}
          >
            {icon}{label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default YourGrowth;
